import { authenticatedProApiFetch } from "./pro-api.client";

export interface OrgCertEnrollRole {
  id: string;
  name: string;
  label: string | null;
  color: string | null;
}

export interface OrgCertMemberModuleProgress {
  moduleId: string;
  title: string;
  trophyImageUrl: string | null;
  progressPercent: number;
  status: string;
  completedAt: string | null;
  expiresAt: string | null;
  certificateNumber?: string | null;
}

export interface OrgCertMember {
  userId: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: {
    id: string;
    label: string | null;
    color: string | null;
  } | null;
  modules: OrgCertMemberModuleProgress[];
}

export interface OrgCertPendingInvite {
  inviteId: string;
  email: string;
  roleId: string;
  roleLabel: string | null;
  roleColor: string | null;
  intent: "membership" | "certification";
  expiresAt: string;
  createdAt: string;
}

export interface OrgCertAccessRequestUser {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface OrgCertAccessRequest {
  id: string;
  userId: string;
  organizationId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  user: OrgCertAccessRequestUser | null;
}

export interface CertReminderOverride {
  moduleId?: string;
  trainingId?: string;
}

export interface CertReminderResult {
  userId: string;
  ok: boolean;
  error?: string;
  moduleId?: string;
  trainingId?: string;
  trainingTitle?: string;
  moduleTitle?: string;
}

/** GET /organizations/:id/certifications/members */
export async function fetchOrgCertificationMembers(
  accessToken: string,
  orgId: string,
): Promise<{
  members: OrgCertMember[];
  enrollRoles: OrgCertEnrollRole[];
  pendingInvites: OrgCertPendingInvite[];
}> {
  const data = await authenticatedProApiFetch<{
    members?: OrgCertMember[];
    enrollRoles?: OrgCertEnrollRole[];
    pendingInvites?: OrgCertPendingInvite[];
  }>(`/organizations/${orgId}/certifications/members`, accessToken);
  return {
    members: Array.isArray(data.members) ? data.members : [],
    enrollRoles: Array.isArray(data.enrollRoles) ? data.enrollRoles : [],
    pendingInvites: Array.isArray(data.pendingInvites)
      ? data.pendingInvites
      : [],
  };
}

/** GET /organizations/:id/users/:userId/certifications */
export async function fetchOrgUserCertifications(
  accessToken: string,
  orgId: string,
  userId: string,
): Promise<OrgCertMember> {
  return authenticatedProApiFetch(
    `/organizations/${orgId}/users/${userId}/certifications`,
    accessToken,
  );
}

/** POST /organizations/:id/certifications/reminders */
export async function sendOrgCertificationReminders(
  accessToken: string,
  orgId: string,
  body: {
    userIds: string[];
    overrides?: Record<string, CertReminderOverride>;
  },
): Promise<{ sent: number; results: CertReminderResult[] }> {
  return authenticatedProApiFetch(
    `/organizations/${orgId}/certifications/reminders`,
    accessToken,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** GET /organizations/:id/certification-access-requests */
export async function fetchOrgCertificationAccessRequests(
  accessToken: string,
  orgId: string,
): Promise<OrgCertAccessRequest[]> {
  const data = await authenticatedProApiFetch<{
    requests: OrgCertAccessRequest[];
  }>(`/organizations/${orgId}/certification-access-requests`, accessToken);
  return Array.isArray(data.requests) ? data.requests : [];
}

/** POST /organizations/:id/certification-access-requests */
export async function createOrgCertificationAccessRequest(
  accessToken: string,
  orgId: string,
): Promise<OrgCertAccessRequest> {
  return authenticatedProApiFetch(
    `/organizations/${orgId}/certification-access-requests`,
    accessToken,
    { method: "POST" },
  );
}

/** POST /organizations/:id/certification-access-requests/:userId/approve */
export async function approveOrgCertificationAccessRequest(
  accessToken: string,
  orgId: string,
  userId: string,
  body?: { roleId?: string },
): Promise<{ message: string; roleId?: string }> {
  return authenticatedProApiFetch(
    `/organizations/${orgId}/certification-access-requests/${userId}/approve`,
    accessToken,
    { method: "POST", body: JSON.stringify(body ?? {}) },
  );
}

/** POST /organizations/:id/certification-access-requests/:userId/deny */
export async function denyOrgCertificationAccessRequest(
  accessToken: string,
  orgId: string,
  userId: string,
): Promise<{ message: string }> {
  return authenticatedProApiFetch(
    `/organizations/${orgId}/certification-access-requests/${userId}/deny`,
    accessToken,
    { method: "POST" },
  );
}
